import { useEffect, useState, useRef } from "react";
import { useQueries } from '@tanstack/react-query';
import useAuthentication from "../../hooks/useAuthentication.hook";
import NeoGridContainer from "../../design/design_components/neo/layout/NeoGridContainer.base";
import NeoCard from "../../design/design_components/neo/panel/NeoCard.base";
import NeoColumn from "../../design/design_components/neo/layout/NeoColumn.base";
import ContainedImages from "../../components/ContainedImages.component";
import { DateTime } from "luxon";
import Icono from "../../design/assets/img/bcn/live.png";
import MapReset from "../../design/assets/img/bcn/map_reset.png";
import Circle from "../../components/Circle.component";
import Avatar from "../../components/Avatar.component";
import AbsoluteText from "../../components/AbsoluteText.component";
import NeoTable from "design/design_components/neo/table/NeoTable.base";
import NeoDropdown from "design/design_components/neo/form/NeoDropdown.base";
import * as BeaconService from "../../services/beacon.service";
import {
  TransformWrapper,
  TransformComponent,
  useControls,
} from "react-zoom-pan-pinch";

const COLOR = {
  ACTIVE: '143, 190, 154',
  INACTIVE: '17, 156, 212',
  DISCONNECTED: '195, 195, 195'
}

const Controls = () => {
  const { zoomIn, zoomOut, resetTransform } = useControls();

  return (
    <div className="tools-zoom p-col-1 p-col-align-center">
      <div className="p-grid p-dir-col p-mx-auto">
        <button onClick={() => zoomIn()}>+</button>
        <hr/>
        <button onClick={() => zoomOut()}>-</button>
        <hr/>
        <button onClick={() => resetTransform()}><img src={MapReset}/></button>
      </div>
    </div>
  );
};

function ocultarMitadID(id) {
  let str = String(id);
  let length = str.length;
  
  if (length > 8) {
    str = str.slice(-8);
    length = str.length;
  }

  const mitad = Math.floor(length / 2);
  const oculto = '*'.repeat(mitad);
  const visible = str.slice(mitad);
  return oculto + visible;
}

export default function ActivityRealTime() {
  const results = useQueries({
    queries: [
      {
        queryKey: ['floors'],
        queryFn: async () => {
          return [
            {
              id: 1,
              name: "America Digital: Stand",
              url: "https://firebasestorage.googleapis.com/v0/b/neo-mnv-prod.appspot.com/o/neowifi%2Fplans%2FjbukGvOcs1RbBV9dbt2MANDrYAn2%2Fmapa-america-digital.jpg?alt=media&token=2d8908ad-f05a-4143-9575-b24b34198731"
            },
          ]
        },
        placeholderData: [],
      },
      {
        queryKey: ['beacons'],
        queryFn: BeaconService.get,
        select: (response) => response.payload.beacons,
        placeholderData: [],
      },
    ]
  });

  const isLoading = results.some(result => result.isLoading);
  const isError = results.some(result => result.isError);
  const isSuccess = results.every(result => result.isSuccess);

  const [floors, beacons] = results.map(result => result.data);

  const [currentFloor, setCurrentFloor] = useState();
  const authentication = useAuthentication();
  const [allImagesLoaded, setAllImagesLoaded] = useState(false);
  const [img, setImg] = useState({
    clientWidth: 0,
    clientHeight: 0,
    naturalWidth: 0,
    naturalHeight: 0,
    spacingHorizontal: 0,
    spacingVertical: 0,
  });
  const beaconsRef = useRef([]);
  const [events, setEvents] = useState({});
  const [detections, setDetections] = useState([]);
  const [expandedRows, setExpandedRows] = useState([]);

  useEffect(() => {
    if (isSuccess && floors.length > 0 && !currentFloor) {
      setCurrentFloor(floors[0]);
    }
  }, [isSuccess, floors, currentFloor]);

  useEffect(() => {
    beaconsRef.current = beacons;
  }, [beacons]);

  // useEffect(() => {
  //   (async () => {
  //     setLoading(true);
  //     const BeaconServiceResponse = await ();
  //     console.log("beacon service response", BeaconServiceResponse);

  //     // const url = new URL(`${process.env.REACT_APP_SERVER_URL_BASE}/beacons`);
  //     // const response = await fetch(url, {
  //     //   method: "GET",
  //     // }).finally(() => {
  //     //   setLoading(false);
  //     // });
  //     // if (response.ok) {
  //     //   const data = await response.json();
  //     //   setBeacons(data.beacons);
  //     // }
  //   })().catch((error) => {
  //     console.error('Error al cargar la lista de beacons', error);
  //   })
  // }, [])

  useEffect(() => {
    if (authentication.socket && authentication.statusSocket === 'CONNECTED') {
      Object.entries(socketListeners).forEach(([eventName, eventFn]) => {
        authentication.socket.on(eventName, eventFn);
      })
    }
    return () => {
      Object.entries(socketListeners).forEach(([eventName]) => {
        if (authentication.socket) {
          authentication.socket.off(eventName);
        }
      })
    }
  }, [authentication.statusSocket, authentication.socket])

  useEffect(() => {
    if (events) {
      const detections = [];
      Object.entries(events).forEach(([key, value], index) => {
        if (Array.isArray(value.connections) && value.connections.length > 0) {
          const beacon = beacons.find((beacon) => beacon.id === parseInt(key));
          if (beacon) {
            value.connections.map((connection, index) => {
              detections.push({
                beacon,
                connection
              });
            })
          }
        }
      });
      setDetections(detections);
    }
  }, [events])

  const handlerBeaconEnterAndExit = (event, type) => {
    setEvents((events) => {
      const eventsShadowCopy = { ...events };

      const contact = {
        authUid: event.authUid,
        email: event.email,
        identifierForVendor: event.identifierForVendor,
      };
      
      const beacon = beaconsRef.current.find((beacon) => beacon.minor === event.minor);
      if (beacon) {
        // Clear old user
        Object.entries(eventsShadowCopy).forEach(([key, value], index) => {
          const { connections } = value;
          if (Array.isArray(connections)) {
            value.connections = connections.filter((connection) => {
              const isContact = connection.contact.authUid === contact.authUid;
              if (isContact) {
                if ( (type === 'ranged-beacons')
                  || (type === 'did-enter-beacon-region')
                  || (type === 'did-exit-beacon-region' && parseInt(key) === beacon.id)
                ) {
                  return !true; // Borralo de la lista
                }
              }
              return !false;
            })
          }
        });
  
        if (eventsShadowCopy[beacon.id] === undefined) {
          eventsShadowCopy[beacon.id] = {
            connections: []
          }
        }

        switch (type) {
          case 'ranged-beacons':
            eventsShadowCopy[beacon.id].connections.push({
              type,
              contact,
              accuracy: event.accuracy,
              createdAt: event.createdAt,
            });
            break;
          case 'did-enter-beacon-region':
            eventsShadowCopy[beacon.id].connections.push({
              type,
              contact,
              createdAt: event.createdAt,
            });
            break;
          default:
            break;
        }

      }

      return eventsShadowCopy;
    });
  }

  const socketListeners = {
    'ranged-beacons': (event) => {
      handlerBeaconEnterAndExit(event, 'ranged-beacons');
    },
    'did-enter-beacon-region': (event) => {
      handlerBeaconEnterAndExit(event, 'did-enter-beacon-region');
    },
    'did-exit-beacon-region': (event) => {
      handlerBeaconEnterAndExit(event, 'did-exit-beacon-region');
    },
  }

  const convertToDistanceLabel = (accuracy) => {
    if (accuracy < 1) {
      const centimetros = Math.round(accuracy * 100);
      return `${centimetros} cm`;
    } else {
      const m = Math.floor(accuracy);
      const cm = Math.round((accuracy - m) * 100);
      return `${m} m ${cm} cm`;
    }
  }

  const headerTemplate = (node, a, b, c) => {
    const { beacon } = node;
    return <span>{beacon.deparment_name} ({calculateConnectionsTotalByBeaconId(beacon.id)})</span>;
  }

  const calculateConnectionsTotalByBeaconId = (id) => {
    return events[id]?.connections?.length || 0;
  }

  return (
    <>
      <div className="p-col-12">
        <div className="p-grid p-pl-4 p-pt-4 p-pr-4 p-align-center">
          <div className="p-col-12">
            <div className="p-d-flex">
              <img width="45" src={Icono} />
              <h1 className="titulo-pagina-principal p-pl-2">Actividad en vivo</h1>
            </div>
          </div>
          <div className="p-col-5">
            <div className="p-d-flex">
              <NeoDropdown 
                label="Periódo"
                placeholder="Periódo" 
                options={[{
                  name: 'En vivo',
                  value: 1
                }]}
                optionLabel="name"
                optionValue="value"
                value={1}
              />
              <NeoDropdown 
                label="Sucursal"
                placeholder="Sucursal" 
                options={[{
                  name: 'America Digital',
                  value: 1
                }]} 
                optionLabel="name"
                optionValue="value"
                value={1}
              />
              <NeoDropdown
                label="Nivel"
                placeholder="Nivel" 
                options={[{
                  name: 1,
                  value: 1
                }]} 
                optionLabel="name"
                optionValue="value"
                value={1}
              />
            </div>
          </div>
        </div>
      </div>

      <NeoGridContainer>
        <NeoColumn extra="main-card" md={12}>
          <NeoCard extra="card-map" style={{ height: "100%" }}>
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "3fr 2fr",
                width: "100%",
                height: "100%",
                padding: "0rem 1rem 0rem 1rem",
              }}
            >
              <div style={{ display: "flex", flexDirection: "column" }}>
                {/* {currentFloor && 
                  <NeoTitleMain extra="trajectory-title" title={ currentFloor.name } />
                } */}
                <TransformWrapper
                  initialScale={1}
                  initialPositionX={200}
                  initialPositionY={100}
                >
                  {({ zoomIn, zoomOut, resetTransform, ...rest }) => (
                    <div className="p-grid">
                      <div className="p-col-11 p-py-5 p-col-align-center">
                        <TransformComponent>
                          <div className="preview-container" style={{ position: "relative", height: "600px" }}>

                            {/* Planos */}
                            {currentFloor &&
                              <ContainedImages
                                allImagesLoaded={allImagesLoaded}
                                setAllImagesLoaded={setAllImagesLoaded}
                                floors={floors}
                                currentFloor={currentFloor}
                                setImg={setImg}
                              />
                            }

                            {/* Lista de Beacons */}
                            <div
                              style={{
                                width: `${img.clientWidth}px`,
                                height: `${img.clientHeight - img.spacingVertical}px`,
                                position: "absolute",
                                top: 0,
                                left: img.spacingHorizontal / 2,
                              }}
                            >
                              {allImagesLoaded &&
                                beacons
                                  .filter(
                                    (beacon) =>
                                      beacon.store_floor_id === currentFloor.id
                                  )
                                  .filter(
                                    (beacon) =>
                                      Boolean(beacon.coord_x) &&
                                      beacon.coord_x > 0 &&
                                      Boolean(beacon.coord_y) &&
                                      beacon.coord_y > 0
                                  )
                                  .map((beacon, i) => {
                                    let color = COLOR.INACTIVE;
                                    if (['CONNECTING', 'DISCONNECTED'].includes(authentication.statusSocket)) {
                                      color = COLOR.DISCONNECTED;
                                    }

                                    const hasConnections =
                                      Array.isArray(events[beacon.id]?.connections)
                                      && events[beacon.id].connections.length > 0;
                                    
                                    return <>
                                      <AbsoluteText
                                        id={beacon.id}
                                        img={img}
                                        coord_x={beacon.coord_x}
                                        coord_y={beacon.coord_y-70}
                                        text={beacon.name}
                                      />
                                      <Circle
                                        id={beacon.id}
                                        tooltip={beacon.deparment_name}
                                        color={color}
                                        img={img}
                                        coord_x={beacon.coord_x}
                                        coord_y={beacon.coord_y}
                                      />
                                      {hasConnections && <>
                                        <AbsoluteText
                                          id={beacon.id}
                                          img={img}
                                          coord_x={beacon.coord_x}
                                          coord_y={beacon.coord_y - 100}
                                          text={
                                            <span
                                              style={{
                                                background: 'rgb(0 157 99 / 79%)',
                                                color: 'white',
                                                borderRadius: '5px',
                                                padding: '2px'
                                              }}
                                            >
                                              {events[beacon.id].connections.length}
                                            </span>
                                          }
                                        />
                                        <Avatar
                                          id={beacon.id}
                                          img={img}
                                          coord_x={beacon.coord_x}
                                          coord_y={beacon.coord_y}
                                          tooltip={beacon.deparment_name}
                                        />
                                      </>}
                                    </>
                                  })}
                            </div>
                          </div>
                        </TransformComponent>
                      </div>
                      <div className="p-col-1 p-col-align-center p-py-5">
                        <Controls />
                      </div>
                    </div>
                  )}
                </TransformWrapper>
              </div>
              <div className="right-col">
                {/* <NeoTitleMain extra="p-jc-center" title={"Detecciones"} /> */}
                <div className="table">
                  <NeoTable
                    value={detections}
                    emptyMessage=""
                    scrollable
                    scrollHeight="450px"
                    rowGroupMode="subheader"
                    groupField="beacon.id"
                    sortMode="single"
                    sortField="beacon.deparment_name"
                    sortOrder={1}
                    expandableRowGroups
                    expandedRows={expandedRows}
                    onRowToggle={(e) => setExpandedRows(e.data)}
                    rowGroupHeaderTemplate={headerTemplate}
                    rowGroupFooterTemplate={() => { }}
                    stripedRows
                    dataKey="connection.contact.authUid"
                  >
                    <NeoColumn field="connection.contact.email" header="Departamento / Usuario" style={{width: '25%'}}/>
                    <NeoColumn field="connection.contact.identifierForVendor" header="ID negocio" style={{ width: '15%' }} body={(node, column) => {
                      if (node && node.connection && node.connection.contact && node.connection.contact.identifierForVendor) {
                        return ocultarMitadID(node.connection.contact.identifierForVendor);
                      }
                      return null;
                    }} />
                    <NeoColumn field="connection.createdAt" header="Hora" style={{ width: '15%' }} body={(node, column) => {
                      if (node.connection.createdAt !== undefined) {
                        const createdAt = DateTime.fromISO(node.connection.createdAt);
                        return <span>{createdAt.toLocaleString(DateTime.TIME_WITH_SECONDS)}</span>
                      }
                      return null;
                    }} />
                    <NeoColumn field="connection.accuracy" header="Distancia" style={{ width: '10%' }} body={(node, column) => {
                      return node.connection.accuracy && node.connection.accuracy !== -1
                        ? <span>{convertToDistanceLabel(node.connection.accuracy)}</span>
                        : '--';
                    }} />
                    <NeoColumn field="connection.type" header="Tipo" style={{ width: '10%' }} body={(node, column) => {
                      const type = node.connection.type === 'ranged-beacons' && node.connection.accuracy !== -1
                        ? 'R'
                        : 'M';
                      
                      const color = node.connection.type === 'ranged-beacons' && node.connection.accuracy !== -1
                        ? '#FB640C'
                        : '#072440';
                      
                      return <span style={{ color }}>{type}</span>;
                    }} />
                  </NeoTable>
                </div>
              </div>
            </div>
          </NeoCard>
        </NeoColumn>
      </NeoGridContainer>
    </>
  );
}